import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  AlertTriangle, 
  Search, 
  Filter,
  Users,
  TrendingUp,
  Clock,
  MapPin
} from 'lucide-react';
import { usePatients, usePatientMutations, usePatientStatistics } from '../../hooks/usePatients';
import { usePatientRiskFactors } from '../../hooks/usePatientRiskFactors';
import PatientForm from './PatientForm';
import './PatientList.css';

const PatientList = ({ departmentFilter = null }) => {
  const { t } = useTranslation('patientRisk');
  const [showForm, setShowForm] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskLevelFilter, setRiskLevelFilter] = useState('all');
  
  const { patients, loading, refresh } = usePatients({
    departmentId: departmentFilter,
    status: statusFilter !== 'all' ? statusFilter : undefined
  });
  
  const { statistics, loading: statsLoading } = usePatientStatistics(departmentFilter);
  const { deletePatient } = usePatientMutations();
  const { factors: allRiskFactors } = usePatientRiskFactors({ activeOnly: true });
  
  const handleAddPatient = () => {
    setSelectedPatient(null);
    setShowForm(true);
  };
  
  const handleEditPatient = (patient) => {
    setSelectedPatient(patient.id);
    setShowForm(true);
  };
  
  const handleDeletePatient = async (patientId) => {
    if (!window.confirm(t('confirmDeletePatient'))) {
      return;
    }
    
    try {
      await deletePatient(patientId);
      refresh();
    } catch (error) {
      alert(t('failedToDeletePatient'));
    }
  };
  
  const handleFormSave = () => {
    setShowForm(false);
    setSelectedPatient(null);
    refresh();
  };
  
  const getRiskLevelForPatient = (patient) => {
    const riskFactorCount = patient.riskFactorIds?.length || 0;
    
    if (riskFactorCount === 0) return 'minimal';
    if (riskFactorCount === 1) return 'low';
    if (riskFactorCount === 2) return 'medium';
    if (riskFactorCount >= 3) return 'high';
    
    return 'minimal';
  };
  
  const getRiskLevelColor = (level) => {
    const colors = {
      minimal: '#4caf50',
      low: '#8bc34a',
      medium: '#ff9800',
      high: '#f44336',
      critical: '#c62828'
    };
    return colors[level] || colors.minimal;
  };
  
  const getRiskFactorNames = (riskFactorIds) => {
    if (!riskFactorIds || riskFactorIds.length === 0) {
      return 'None';
    }
    
    const factorNames = riskFactorIds
      .map(id => {
        const factor = allRiskFactors.find(f => f.id === id);
        return factor ? factor.name : null;
      })
      .filter(name => name !== null);
    
    return factorNames.join(', ');
  };
  
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString();
  };
  
  const filteredPatients = patients.filter(patient => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!patient.patientIdentifier.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // Risk level filter
    if (riskLevelFilter !== 'all') {
      const patientRiskLevel = getRiskLevelForPatient(patient);
      if (patientRiskLevel !== riskLevelFilter) {
        return false;
      }
    }
    
    return true;
  });
  
  return (
    <div className="patient-list-container">
      <div className="patient-list-header">
        <div className="header-title">
          <h1>
            <Users size={28} />
            {t('patientManagement')}
          </h1>
          <p>{t('trackPatientAdmissions')}</p>
        </div>
        <button onClick={handleAddPatient} className="btn btn-primary">
          <UserPlus size={20} />
          {t('addPatient')}
        </button>
      </div>
      
      {/* Statistics Cards */}
      {!statsLoading && statistics && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#e3f2fd' }}>
              <Users size={24} color="#2196F3" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.admitted}</div>
              <div className="stat-label">Currently Admitted</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#ffebee' }}>
              <AlertTriangle size={24} color="#f44336" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.highRisk}</div>
              <div className="stat-label">{t('highRiskPatients')}</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#fff3e0' }}>
              <TrendingUp size={24} color="#ff9800" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.avgRiskFactors.toFixed(1)}</div>
              <div className="stat-label">Avg Risk Factors</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: '#f3e5f5' }}>
              <MapPin size={24} color="#9c27b0" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{statistics.withIsolation}</div>
              <div className="stat-label">{t('isolationRequired')}</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Filters and Search */}
      <div className="patient-filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by patient identifier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <Filter size={20} />
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('allStatuses')}</option>
            <option value="admitted">{t('admitted')}</option>
            <option value="discharged">{t('discharged')}</option>
            <option value="transferred">{t('transferred')}</option>
          </select>
          
          <select 
            value={riskLevelFilter} 
            onChange={(e) => setRiskLevelFilter(e.target.value)}
          >
            <option value="all">{t('allRiskLevels')}</option>
            <option value="minimal">{t('minimalRisk')}</option>
            <option value="low">{t('lowRisk')}</option>
            <option value="medium">{t('mediumRisk')}</option>
            <option value="high">{t('highRisk')}</option>
          </select>
        </div>
      </div>
      
      {/* Patients Table */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading patients...</p>
        </div>
      ) : filteredPatients.length === 0 ? (
        <div className="empty-state">
          <Users size={48} color="#ccc" />
          <h3>No patients found</h3>
          <p>
            {searchQuery || riskLevelFilter !== 'all' 
              ? 'Try adjusting your filters'
              : 'Add your first patient to get started'}
          </p>
        </div>
      ) : (
        <div className="patients-table-container">
          <table className="patients-table">
            <thead>
              <tr>
                <th>{t('patientId')}</th>
                <th>{t('location')}</th>
                <th>{t('admissionDate')}</th>
                <th>{t('isolationLevel')}</th>
                <th>{t('riskFactors')}</th>
                <th>{t('riskLevel')}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPatients.map(patient => {
                const riskLevel = getRiskLevelForPatient(patient);
                const riskColor = getRiskLevelColor(riskLevel);
                
                return (
                  <tr key={patient.id}>
                    <td>
                      <strong>{patient.patientIdentifier}</strong>
                    </td>
                    <td>
                      <div className="location-info">
                        <MapPin size={14} />
                        Bed: {patient.bedId?.substring(0, 8)}...
                      </div>
                    </td>
                    <td>
                      <div className="date-info">
                        <Clock size={14} />
                        {formatDate(patient.admissionDate)}
                      </div>
                    </td>
                    <td>
                      <span className={`isolation-badge ${patient.isolationLevel.toLowerCase().replace(/\+/g, '-')}`}>
                        {patient.isolationLevel}
                      </span>
                    </td>
                    <td>
                      <div className="risk-factors-cell">
                        {t('factorsCount', { count: patient.riskFactorIds?.length || 0 })}
                        {patient.riskFactorIds?.length > 0 && (
                          <div className="risk-factors-tooltip">
                            {getRiskFactorNames(patient.riskFactorIds)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span 
                        className="risk-level-badge"
                        style={{ backgroundColor: riskColor }}
                      >
                        {riskLevel.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${patient.status}`}>
                        {patient.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleEditPatient(patient)}
                          className="btn btn-edit btn-icon"
                          title="Edit patient"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeletePatient(patient.id)}
                          className="btn btn-delete btn-icon"
                          title={t('deletePatient')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Patient Form Modal */}
      {showForm && (
        <PatientForm
          patientId={selectedPatient}
          onClose={() => {
            setShowForm(false);
            setSelectedPatient(null);
          }}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
};

export default PatientList;

